import { useEffect, useState } from 'react';
import { apiRequest } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

/** Resolved plan entitlements for the active org (token-billing §5c). */
export interface Entitlements {
  tokensPerMonth: number;
  tokensUnlimited: boolean;
  ownDeviceTokenRate: number;
  maxUsers: number;
  apiAccess: boolean;
  aiAssistant: boolean;
  schedulesEnabled: boolean;
  reportTier: 'basic' | 'advanced' | 'unlimited';
  supportTier: string;
  myDevices: { enabled: boolean; includedDevices: number; maxDevices?: number };
}

// Permissive defaults so the UI never hides a feature before entitlements load.
const PERMISSIVE: Entitlements = {
  tokensPerMonth: 0, tokensUnlimited: true, ownDeviceTokenRate: 0, maxUsers: 0,
  apiAccess: true, aiAssistant: true, schedulesEnabled: true, reportTier: 'unlimited',
  supportTier: 'standard', myDevices: { enabled: true, includedDevices: 0 },
};

/**
 * Loads the active org's entitlements from `GET /organizations/:id/entitlements`.
 * Returns permissive defaults while loading / on error so UI gating fails open
 * (matches the backend, which only enforces once TOKEN_ENFORCEMENT is on).
 */
export function useEntitlements(): { entitlements: Entitlements; plan: string | null; loading: boolean } {
  const { activeOrganization } = useAuth();
  const orgId = activeOrganization?.id;
  const [state, setState] = useState<{ entitlements: Entitlements; plan: string | null; loading: boolean }>({
    entitlements: PERMISSIVE, plan: null, loading: true,
  });

  useEffect(() => {
    if (!orgId) { setState({ entitlements: PERMISSIVE, plan: null, loading: false }); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest<{ plan: string; entitlements: Entitlements }>(
          `/organizations/${orgId}/entitlements`, { method: 'GET' }, orgId,
        );
        if (!cancelled) setState({ entitlements: { ...PERMISSIVE, ...res.entitlements }, plan: res.plan, loading: false });
      } catch {
        if (!cancelled) setState({ entitlements: PERMISSIVE, plan: null, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  return state;
}
